import { useState, useEffect, useCallback } from 'react'
import {
  WbsItemEntity,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import {
  WbsItemFormModel,
  WbsItemInitialValue,
  createInitialValue,
  toDeltaInput,
} from '../model'
import { useWbsItemRepository } from '../../../../services/adaptors/wbsItemRepositoryAdaptor'
import { useDispatch, useSelector } from 'react-redux'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { AllState } from '../../../../store'
import { usePageDataIsEdited } from '../../../hooks/usePageDataIsEdited'
import {
  wbsItemAdditionalPropertyValuesVoService,
  WbsItemAdditionalPropertyValueType,
} from '../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { WbsItemAdditionalPropertyEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { useEditedFormData } from '../../../containers/SingleSheetV2/hooks/editedFormData'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { extractValuesFromResponse } from '../../../../lib/commons/api'
import { handleWarning } from '../../../../handlers/globalErrorHandler'

export const useWbsItemSingleSheetData = (
  uuid: string,
  type: WbsItemType,
  typeVo: WbsItemTypeVO
) => {
  const { getDetail, updateDelta } = useWbsItemRepository()
  const [initialized, setInitialized] = useState<boolean>(false)
  const { edited, editedPageData, refreshedPageData } = usePageDataIsEdited()
  const {
    editedFormData,
    editFormData,
    refreshEditedFormData,
    someSomeDataIsEdited,
  } = useEditedFormData()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [entity, setEntity] = useState<WbsItemEntity | undefined>()
  const [formModel, setFormModel] = useState<WbsItemFormModel>(
    createInitialValue(type, typeVo)
  )
  const fetchData = useCallback(async () => {
    setIsLoading(true)
    const response = await getDetail(uuid)
    setEntity(response)
    setFormModel(structuredClone(response))
    setInitialized(true)
    setIsLoading(false)
    refreshedPageData()
    refreshEditedFormData()
  }, [getDetail, uuid, refreshedPageData, refreshEditedFormData])
  useEffect(() => {
    fetchData()
  }, [fetchData, uuid])
  const onChange = useCallback(
    <K extends keyof WbsItemFormModel, T extends WbsItemFormModel[K]>(
      path: K,
      value: T
    ) => {
      setFormModel(prev => {
        const newModel = {
          ...prev,
        }
        newModel[path] = value
        return newModel
      })
      editedPageData()
      editFormData(path)
    },
    [editedPageData, editFormData]
  )
  const onChangeAdditionalPropertyValue = useCallback(
    (
      wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
      value: WbsItemAdditionalPropertyValueType
    ) => {
      setFormModel(prev => {
        let vo =
          prev.additionalPropertyValues ||
          wbsItemAdditionalPropertyValuesVoService.construct()
        wbsItemAdditionalPropertyValuesVoService.setValue(
          vo,
          wbsItemAdditionalProperty,
          value
        )
        return {
          ...prev,
          additionalPropertyValues: vo,
        }
      })
      editedPageData()
      editFormData(wbsItemAdditionalProperty.uuid)
    },
    [editedPageData, editFormData]
  )
  const dispatch = useDispatch()
  const submit = useCallback(async () => {
    if (!entity) return
    setIsLoading(true)
    const input = toDeltaInput(entity, formModel)
    const request = {
      input,
      watchers: (formModel.watchers || []).map(watcher => watcher.uuid),
      tags: {
        wbsItemUuid: uuid,
        tagUuids: (formModel.tags || []).map(tag => tag.uuid),
      },
    }
    const { response } = await updateDelta(request)
    if (response.hasWarning) {
      const messages = extractValuesFromResponse(response.json, 'messages')
      handleWarning(messages, _ => ({
        code: formModel.code,
        name: formModel.displayName,
      }))
    } else {
      dispatch(
        addScreenMessage('wbsItem', {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'update.completed.title' }),
        })
      )
      await fetchData()
    }
    setIsLoading(false)
  }, [dispatch, entity, fetchData, formModel, updateDelta, uuid])
  return {
    initialized,
    edited: edited || someSomeDataIsEdited, // TODO Remove someSomeDataIsEdited because "edited"(requiredSave in Redux store) is not working properly if users open project plan tab.
    editedFormData,
    isLoading,
    formModel,
    onChange,
    onChangeAdditionalPropertyValue,
    reload: fetchData,
    submit,
  }
}
